import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { initLiff, getLineProfile, loginWithLine, broadcastLoginApproved } from '../../lib/lineAuth';

const MobileQrConfirmCard = ({ token, onReturnHome }) => {
  const [loading, setLoading] = useState(true);
  const [lineProfile, setLineProfile] = useState(null);
  const [staffUser, setStaffUser] = useState(null);
  const [approving, setApproving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const isDevStaging =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'dev.fangdent.com' || process.env.REACT_APP_ENV === 'staging');

  // Check initial token status
  const checkTokenStatus = useCallback(async () => {
    if (!token || !supabase) return false;
    try {
      const { data, error } = await supabase.rpc('check_login_token_status', { p_token: token });
      if (!error && Array.isArray(data) && data.length > 0) {
        if (data[0].status === 'approved') {
          setIsSuccess(true);
          return true;
        }
      }
    } catch (err) {
      console.warn('checkTokenStatus error:', err);
    }
    return false;
  }, [token]);

  // Load LINE profile and look up bound staff in Supabase
  const loadProfileAndStaff = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const alreadyApproved = await checkTokenStatus();
      if (alreadyApproved) {
        setLoading(false);
        return;
      }

      await initLiff();
      const profile = await getLineProfile();

      if (profile && profile.userId) {
        setLineProfile(profile);

        // Look up user in user_profiles by line_user_id
        if (supabase) {
          const { data: userData, error: userError } = await supabase.rpc('get_user_by_line_id', {
            p_line_id: profile.userId
          });

          if (!userError && Array.isArray(userData) && userData.length > 0) {
            setStaffUser(userData[0]);
          } else {
            // Fallback direct query on user_profiles
            const { data: directData } = await supabase
              .from('user_profiles')
              .select('id, email, name, role, first_name, last_name, line_user_id')
              .eq('line_user_id', profile.userId)
              .maybeSingle();

            if (directData) {
              setStaffUser(directData);
            }
          }
        }
      }
    } catch (err) {
      console.error('MobileQrConfirmCard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [checkTokenStatus]);

  useEffect(() => {
    loadProfileAndStaff();
  }, [loadProfileAndStaff]);

  const handleApprove = async () => {
    if (!token) return;
    if (!lineProfile || !lineProfile.userId) {
      loginWithLine({ redirectUri: window.location.href });
      return;
    }

    setApproving(true);
    setErrorMessage('');
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      const { data, error } = await supabase.rpc('approve_qr_login', {
        p_token: token,
        p_line_user_id: lineProfile.userId,
        p_line_display_name: lineProfile.displayName || 'LINE User',
        p_line_picture_url: lineProfile.pictureUrl || ''
      });

      if (error) {
        setErrorMessage(error.message || 'เกิดข้อผิดพลาดในการยืนยันตัวตน');
      } else if (data && data.success === false) {
        setErrorMessage(data.message || 'ไม่สามารถยืนยันตัวตนได้');
      } else {
        // Send Realtime Broadcast to notify desktop instantly
        await broadcastLoginApproved(token, {
          user: staffUser || {
            line_user_id: lineProfile.userId,
            name: lineProfile.displayName,
            avatar_url: lineProfile.pictureUrl
          },
          status: 'approved'
        });

        setIsSuccess(true);
      }
    } catch (err) {
      console.error('Approval exception:', err);
      setErrorMessage(err.message || 'เกิดข้อผิดพลาด');
    } finally {
      setApproving(false);
    }
  };

  const handleClose = () => {
    try {
      window.close();
    } catch (e) {}
    if (onReturnHome) {
      onReturnHome();
    } else if (typeof window !== 'undefined') {
      window.location.href = window.location.origin;
    }
  };

  // 1. Success Screen
  if (isSuccess) {
    return (
      <div
        data-testid="mobile-qr-login-success-view"
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #06C755 0%, #059669 100%)',
          padding: '20px',
          boxSizing: 'border-box'
        }}
      >
        <div
          style={{
            maxWidth: '420px',
            width: '100%',
            backgroundColor: '#ffffff',
            borderRadius: '24px',
            padding: '36px 24px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }}
        >
          {isDevStaging && (
            <div
              data-testid="dev-staging-badge"
              style={{
                marginBottom: '16px',
                padding: '8px 12px',
                backgroundColor: '#fff7ed',
                border: '1.5px solid #f97316',
                borderRadius: '12px',
                color: '#c2410c',
                fontSize: '0.82em',
                fontWeight: '700'
              }}
            >
              🧪 Dev Staging Environment (dev.fangdent.com)
            </div>
          )}
          <div style={{ fontSize: '54px', marginBottom: '14px' }}>🎉</div>
          <h2 style={{ fontSize: '1.4em', fontWeight: 'bold', color: '#15803d', margin: '0 0 10px 0' }}>
            เข้าสู่ระบบบนคอมพิวเตอร์สำเร็จแล้ว!
          </h2>
          <p style={{ color: '#166534', fontSize: '1.05em', fontWeight: 'bold', lineHeight: '1.6', margin: '0 0 8px 0' }}>
            🎉 ยืนยันตัวตนสำเร็จแล้ว! หน้าจอคอมพิวเตอร์กำลังเข้าสู่ระบบ คุณสามารถปิดหน้านี้ได้ทันที
          </p>
          <p style={{ color: '#64748b', fontSize: '0.92em', lineHeight: '1.5', margin: '0 0 24px 0' }}>
            ท่านสามารถกลับไปใช้งานที่หน้าจอคอมพิวเตอร์ได้เลยครับ
          </p>

          <div
            style={{
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '24px'
            }}
          >
            <p style={{ margin: 0, color: '#166534', fontSize: '0.88em', fontWeight: 'bold' }}>
              🏥 ระบบ CSSD โรงพยาบาลทันตกรรม
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#06C755',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1em',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(6, 199, 85, 0.3)'
            }}
          >
            ปิดหน้าต่างนี้ (Close Window)
          </button>
        </div>
      </div>
    );
  }

  // 2. Confirmation View
  const staffDisplayName = staffUser
    ? staffUser.name || `${staffUser.first_name || ''} ${staffUser.last_name || ''}`.trim() || staffUser.email
    : null;

  return (
    <div
      data-testid="mobile-qr-login-confirm-view"
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #06C755 0%, #059669 100%)',
        padding: '20px',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          maxWidth: '420px',
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          padding: '32px 24px',
          boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
          textAlign: 'center'
        }}
      >
        {isDevStaging && (
          <div
            data-testid="dev-staging-badge"
            style={{
              marginBottom: '16px',
              padding: '8px 12px',
              backgroundColor: '#fff7ed',
              border: '1.5px solid #f97316',
              borderRadius: '12px',
              color: '#c2410c',
              fontSize: '0.82em',
              fontWeight: '700'
            }}
          >
            🧪 Dev Staging Environment (dev.fangdent.com)
          </div>
        )}

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            backgroundColor: '#e0f2fe',
            color: '#0284c7',
            fontSize: '28px',
            marginBottom: '16px'
          }}
        >
          🏥
        </div>
        <h2 style={{ fontSize: '1.25em', fontWeight: 'bold', color: '#1e293b', margin: '0 0 6px 0' }}>
          ระบบ CSSD โรงพยาบาลทันตกรรม
        </h2>
        <h3 style={{ fontSize: '1.02em', fontWeight: '600', color: '#059669', margin: '0 0 14px 0' }}>
          ยืนยันการเข้าสู่ระบบบนคอมพิวเตอร์
        </h3>

        <div
          style={{
            background: '#eff6ff',
            border: '1.5px solid #bfdbfe',
            color: '#1d4ed8',
            borderRadius: '12px',
            padding: '12px 14px',
            fontSize: '0.92em',
            fontWeight: '600',
            marginBottom: '18px',
            lineHeight: '1.4'
          }}
        >
          📱 มีคำขอเข้าสู่ระบบบนคอมพิวเตอร์ผ่าน QR Code
        </div>

        {errorMessage && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              color: '#b91c1c',
              fontSize: '0.88em',
              marginBottom: '16px'
            }}
          >
            ⚠️ {errorMessage}
          </div>
        )}

        {/* If not logged into LINE */}
        {!lineProfile ? (
          <div style={{ marginBottom: '20px' }}>
            <div
              data-testid="mobile-qr-login-require-line-prompt"
              style={{
                background: '#fffbeb',
                border: '1.5px solid #fde68a',
                color: '#b45309',
                borderRadius: '14px',
                padding: '16px',
                marginBottom: '16px',
                textAlign: 'left',
                fontSize: '0.92em',
                lineHeight: '1.5'
              }}
            >
              💬 กรุณาเข้าสู่ระบบ LINE เพื่อยืนยันการเข้าสู่ระบบ
            </div>
            <button
              type="button"
              onClick={() => loginWithLine({ redirectUri: window.location.href })}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#06C755',
                color: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '1em',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(6, 199, 85, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}
            >
              <span>💬</span> เข้าสู่ระบบด้วย LINE เพื่อดำเนินการต่อ
            </button>
          </div>
        ) : (
          <div>
            {/* LINE Profile Box */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '14px',
                padding: '14px',
                marginBottom: '16px',
                textAlign: 'left'
              }}
            >
              {lineProfile.pictureUrl ? (
                <img
                  src={lineProfile.pictureUrl}
                  alt="LINE Avatar"
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #06C755'
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '50%',
                    backgroundColor: '#06C755',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px'
                  }}
                >
                  💬
                </div>
              )}
              <div>
                <div style={{ fontSize: '0.78em', color: '#166534' }}>บัญชี LINE ที่ตรวจพบ:</div>
                <div style={{ fontSize: '1em', fontWeight: 'bold', color: '#15803d' }}>
                  {lineProfile.displayName || 'LINE User'}
                </div>
              </div>
            </div>

            {/* Staff Card if bound */}
            {staffUser ? (
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '16px',
                  marginBottom: '20px',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '4px' }}>
                  เข้าใช้งานในชื่อเจ้าหน้าที่:
                </div>
                <div style={{ fontSize: '1.05em', fontWeight: 'bold', color: '#1e293b' }}>
                  {staffDisplayName}
                </div>
                {staffUser.role && (
                  <div style={{ fontSize: '0.85em', color: '#7c3aed', marginTop: '4px', fontWeight: '500' }}>
                    บทบาท: {staffUser.role}
                  </div>
                )}
              </div>
            ) : (
              !loading && (
                <div
                  style={{
                    background: '#fffbeb',
                    border: '1.5px solid #fde68a',
                    borderRadius: '14px',
                    padding: '16px',
                    marginBottom: '20px',
                    textAlign: 'left',
                    color: '#92400e',
                    fontSize: '0.88em',
                    lineHeight: '1.5'
                  }}
                >
                  ⚠️ บัญชี LINE นี้ยังไม่ได้ผูกกับเจ้าหน้าที่ในระบบ กรุณาเข้าสู่ระบบด้วยชื่อผู้ใช้และรหัสผ่านก่อนเพื่อเปิดใช้งานบัญชี
                </div>
              )
            )}

            {/* Action Confirmation Button */}
            <button
              type="button"
              disabled={approving || loading}
              onClick={handleApprove}
              style={{
                width: '100%',
                padding: '15px',
                backgroundColor: approving ? '#94a3b8' : '#06C755',
                color: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '1.05em',
                fontWeight: 'bold',
                cursor: approving ? 'wait' : 'pointer',
                boxShadow: '0 4px 14px rgba(6, 199, 85, 0.35)',
                transition: 'all 0.2s ease',
                marginBottom: '12px'
              }}
            >
              {approving ? '⏳ กำลังยืนยันตัวตน...' : '✅ กดยืนยันตัวตนเพื่อเข้าสู่ระบบ'}
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={handleClose}
          style={{
            width: '100%',
            padding: '12px',
            backgroundColor: '#f1f5f9',
            color: '#475569',
            border: 'none',
            borderRadius: '12px',
            fontSize: '0.92em',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          ยกเลิก / ปิดหน้าต่างนี้
        </button>
      </div>
    </div>
  );
};

export default MobileQrConfirmCard;
